/**
 * Vue mixin för att hantera favoritmarkerade bilar
 * Exponeras som window.niteaCarLikesMixin för att användas i Vue-komponenter
 * 
 * Använder singleton-pattern för att undvika multipla API-anrop
 */
(function() {
    'use strict';

    // API URL (kan överskridas av wp_localize_script)
    const API_URL = (typeof window.niteaCarLikes !== 'undefined' && window.niteaCarLikes.apiUrl)
        ? window.niteaCarLikes.apiUrl
        : `${window.location.protocol}//${window.location.host}/wp-content/plugins/nitea-cars-in-stock/api/likes/cookie-api.php`;

    // Singleton state för att dela mellan alla komponenter
    const LikesManager = {
        likedCars: [],
        isLoading: false,
        isLoaded: false,
        listeners: new Set(),
        pendingRequest: null,

        /**
         * Lägg till lyssnare som får uppdateringar när listan ändras
         */
        addListener(callback) {
            this.listeners.add(callback);
            // Om data redan är laddad, kalla callback direkt
            if (this.isLoaded) {
                callback(this.likedCars);
            }
        },

        /**
         * Ta bort lyssnare
         */
        removeListener(callback) {
            this.listeners.delete(callback);
        },

        /**
         * Meddela alla lyssnare om uppdatering
         */
        notifyListeners() {
            this.listeners.forEach(callback => {
                try {
                    callback(this.likedCars);
                } catch (e) {
                    console.error('Error in likes listener:', e);
                }
            });
        },

        /**
         * Hämta liked cars från API (endast ett anrop åt gången)
         */
        fetch() {
            // Om redan laddar, returnera samma promise
            if (this.pendingRequest) {
                return this.pendingRequest;
            }

            // Om redan laddat, returnera cached data
            if (this.isLoaded) {
                return Promise.resolve(this.likedCars);
            }

            this.isLoading = true;

            this.pendingRequest = new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === XMLHttpRequest.DONE) {
                        this.isLoading = false;
                        this.pendingRequest = null;

                        if (xhr.status === 200) {
                            try {
                                const response = JSON.parse(xhr.responseText);
                                this.likedCars = Array.isArray(response) ? response : [];
                                this.isLoaded = true;
                                this.notifyListeners();
                                resolve(this.likedCars);
                            } catch (e) {
                                console.error('Error parsing liked cars:', e);
                                this.likedCars = [];
                                reject(e);
                            }
                        } else {
                            console.error('Failed to fetch liked cars:', xhr.status);
                            reject(new Error(`HTTP ${xhr.status}`));
                        }
                    }
                };
                
                xhr.onerror = () => {
                    this.isLoading = false;
                    this.pendingRequest = null;
                    console.error('Network error when fetching liked cars');
                    reject(new Error('Network error'));
                };
                
                xhr.open('GET', API_URL, true);
                xhr.send();
            });

            return this.pendingRequest;
        },

        /**
         * Toggle like-status och uppdatera cache
         */
        toggle(carId) {
            return new Promise((resolve, reject) => {
                const id = parseInt(carId, 10);
                
                if (isNaN(id)) {
                    reject(new Error('Invalid car ID'));
                    return;
                }

                const xhr = new XMLHttpRequest();
                const postData = { car: id };
                
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === XMLHttpRequest.DONE) {
                        if (xhr.status === 200) {
                            try {
                                const response = JSON.parse(xhr.responseText);
                                this.likedCars = Array.isArray(response) ? response : [];
                                this.isLoaded = true;
                                this.notifyListeners();
                                resolve(this.likedCars);
                            } catch (e) {
                                console.error('Error parsing like response:', e);
                                reject(e);
                            }
                        } else {
                            console.error('Failed to toggle like:', xhr.status);
                            reject(new Error(`HTTP ${xhr.status}`));
                        }
                    }
                };
                
                xhr.onerror = () => {
                    console.error('Network error when toggling like');
                    reject(new Error('Network error'));
                };
                
                xhr.open('POST', API_URL, true);
                xhr.setRequestHeader('Content-Type', 'application/json');
                xhr.send(JSON.stringify(postData));
            });
        }
    };

    // Definiera mixin
    window.niteaCarLikesMixin = {
        data() {
            return {
                likedCarsArray: []
            };
        },

        created() {
            // Skapa bound callback för att kunna ta bort den senare
            this._likesUpdateCallback = (likedCars) => {
                this.likedCarsArray = likedCars;
            };
        },

        mounted() {
            // Registrera lyssnare för uppdateringar
            LikesManager.addListener(this._likesUpdateCallback);
            
            // Hämta liked cars (singleton säkerställer bara ett anrop)
            this.getLikedCars();
        },

        beforeDestroy() {
            // Ta bort lyssnare när komponenten förstörs
            if (this._likesUpdateCallback) {
                LikesManager.removeListener(this._likesUpdateCallback);
            }
        },

        methods: {
            /**
             * Hämta alla favoritmarkerade bilar från API
             * Använder singleton för att undvika multipla anrop
             * @returns {Promise<number[]>}
             */
            getLikedCars() {
                return LikesManager.fetch().then(likedCars => {
                    this.likedCarsArray = likedCars;
                    return likedCars;
                }).catch(error => {
                    console.error('Failed to get liked cars:', error);
                    this.likedCarsArray = [];
                    return [];
                });
            },

            /**
             * Toggle like-status för en bil
             * Använder singleton för att uppdatera alla komponenter
             * @param {number|string} carId - Bilens ID
             * @returns {Promise<number[]>}
             */
            likeCar(carId) {
                return LikesManager.toggle(carId).then(likedCars => {
                    this.likedCarsArray = likedCars;
                    return likedCars;
                }).catch(error => {
                    console.error('Failed to toggle like:', error);
                    return this.likedCarsArray;
                });
            },

            /**
             * Kontrollera om en bil är favoritmarkerad
             * @param {number|string} carId - Bilens ID
             * @returns {boolean}
             */
            isCarLiked(carId) {
                const id = parseInt(carId, 10);
                if (isNaN(id)) return false;
                return this.likedCarsArray.includes(id);
            }
        }
    };
})();
